import { API_BASE_URL } from "../api/URL_Header";
import { getToken } from "../utils/auth";
import { ROLES, isOwner, isAdmin, canViewAnalytics, canViewTickets, canMutateOrders } from "../utils/roles";

export const LEGACY_ROLES = new Set([ROLES.OWNER, ROLES.ADMIN, ROLES.MODERATOR, ROLES.VIEWER]);

export function isLegacyRole(role) {
  return LEGACY_ROLES.has(role);
}

const PRODUCTION_MANAGER = [
  "panel.access",
  "dashboard.cards.view",
  "dashboard.hero.view",
  "dashboard.production.open",
  "orders.view",
  "orders.search",
  "orders.archive.view",
  "orders.customer-contact.view",
  "orders.bulk-update-status",
  "orders.activity-log.view",
  "designs.search",
  "production.dashboard.view",
  "production.execute",
  "catalog.view",
  "catalog.manage",
  "admin.tools.design-categories.view",
  "admin.tools.design-categories.manage",
  "admin.tools.phone-types.view",
  "admin.tools.phone-types.manage",
  "admin.tools.bulk-status-update",
  "admin.tools.order-activity-log.view",
];

const PRODUCTION_WORKER = [
  "panel.access",
  "dashboard.cards.view",
  "dashboard.production.open",
  "orders.view",
  "orders.search",
  "orders.customer-contact.view",
  "production.dashboard.view",
  "production.execute",
];

const TEMPLATES = {
  ProductionManager: PRODUCTION_MANAGER,
  ProductionWorker: PRODUCTION_WORKER,
};

export function templatePermissions(role) {
  return TEMPLATES[role] || [];
}

export function effectivePermissions(role, permissions) {
  if (Array.isArray(permissions) && permissions.length > 0) return permissions;
  return templatePermissions(role);
}

export function hasPermission(role, permissions, key) {
  if (isLegacyRole(role)) return legacyHas(role, key);
  return effectivePermissions(role, permissions).includes(key);
}

export function hasAnyPermission(role, permissions, keys) {
  return (keys || []).some((key) => hasPermission(role, permissions, key));
}

function legacyHas(role, key) {
  switch (key) {
    case "panel.access":
    case "orders.view":
    case "dashboard.cards.view":
      return role === ROLES.OWNER || role === ROLES.ADMIN || role === ROLES.MODERATOR || role === ROLES.VIEWER;
    case "catalog.manage":
    case "catalog.view":
    case "admin.tools.design-categories.manage":
    case "admin.tools.phone-types.manage":
    case "orders.bulk-update-status":
    case "admin.tools.bulk-status-update":
      return isAdmin(role);
    case "production.execute":
    case "production.dashboard.view":
    case "dashboard.production.open":
      return canMutateOrders(role) && (key !== "production.dashboard.view" && key !== "dashboard.production.open" ? true : isAdmin(role));
    case "orders.activity-log.view":
    case "orders.archive.view":
      return role === ROLES.OWNER || role === ROLES.ADMIN || role === ROLES.MODERATOR || role === ROLES.VIEWER;
    case "analytics.view":
    case "analytics.export":
      return canViewAnalytics(role);
    case "tickets.view":
      return canViewTickets(role);
    case "dashboard.cards.amounts":
      return isOwner(role);
    default:
      if (key.startsWith("analytics.")) return canViewAnalytics(role);
      return false;
  }
}

export async function fetchAccessMe() {
  const response = await fetch(`${API_BASE_URL}/access/me`, {
    headers: { Authorization: `Bearer ${getToken()}` },
  });
  if (!response.ok) throw new Error("access");
  return response.json();
}

export async function fetchAccessCatalog() {
  const response = await fetch(`${API_BASE_URL}/access/catalog`, {
    headers: { Authorization: `Bearer ${getToken()}`, "Content-Type": "application/json" },
  });
  if (!response.ok) throw new Error("تعذر تحميل الصلاحيات");
  return response.json();
}

export async function saveRolePermissions(key, permissions) {
  const response = await fetch(`${API_BASE_URL}/access/roles/${encodeURIComponent(key)}/permissions`, {
    method: "PUT",
    headers: { Authorization: `Bearer ${getToken()}`, "Content-Type": "application/json" },
    body: JSON.stringify({ permissions }),
  });
  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    throw new Error(data.message || "تعذر حفظ الصلاحيات");
  }
  return response.json();
}

export async function createAccessRole(body) {
  const response = await fetch(`${API_BASE_URL}/access/roles`, {
    method: "POST",
    headers: { Authorization: `Bearer ${getToken()}`, "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    throw new Error(data.message || "تعذر إنشاء الدور");
  }
  return response.json();
}
